import { getSpacingByPitch } from "../helpers/getSpacingValue";
import getVergeTiles from "../helpers/getVergeTiles";
import getVergeTilesCoverWidth from "../helpers/getVergeTilesCoverWidth";
import type { Tile, VergeOption } from "../types";
import type {
  Batten,
  Face,
  FaceWithBattens,
  Point,
  Vertex,
  Side
} from "../types/roof";

const calculatePolygonArea = (vertices: Point[]) => {
  let area = 0;
  // Iterate through each edge on the shape, starting with the edge between n-1 and 0
  let j = vertices.length - 1;
  for (let i = 0; i < vertices.length; i++) {
    // eslint-disable-next-line security/detect-object-injection
    area += (vertices[j].x + vertices[i].x) * (vertices[j].y - vertices[i].y);
    j = i;
  }
  return area / 2;
};

export const calculateArea = (faces: Face[]) => {
  let area = 0;
  // Calculate total roof area by considering each face individually first
  for (const face of faces) {
    area += calculatePolygonArea(face.vertices);
  }

  return area;
};

type Intersection = {
  side: Side | undefined;
  bottomOfTileX?: number;
  topOfTileX?: number;
  x?: number;
};

export const battenCalc = ({
  vertices,
  pitch,
  mainTileVariant,
  subtract
}: {
  vertices: Vertex[];
  pitch: number;
  mainTileVariant: Tile;
  subtract: boolean;
}): Batten[] => {
  const allBattens: Batten[] = [];
  const battenSpacing = getSpacingByPitch(
    mainTileVariant.battenSpacings,
    pitch
  );

  // Get rafter length by taking highest y of all points
  const rafterLength = vertices.reduce(
    (prev, curr) => (prev < curr.y ? curr.y : prev),
    0
  );
  // Store list of battens. First batten should account for correct overhang, from height of tile and overhang.
  const firstBatten = battenSpacing.eaveGauge;
  // Calculate the remaining space in the roof with the first batten position and ridge space
  const remainingSpace =
    rafterLength - firstBatten - mainTileVariant.ridgeSpacing;
  // Find the number of battens that are needed at max gauge by finding how many will fit at max gauge and round up by one
  const battenCount = Math.ceil(
    remainingSpace / battenSpacing.maxBattenSpacing
  );

  // Find the adjusted even spacing and check that this is within min gauge
  const spacing = remainingSpace / battenCount;

  if (spacing < mainTileVariant.minBattenSpacing) {
    // eslint-disable-next-line no-console
    console.error("Tiles are at less than min gauge"); // Temporary alert until Error page is built.
  }

  // Place battens at this even spacing up to the top of the roof
  for (let i = 0; i < battenCount + 1; i += 1) {
    const battenHeight = firstBatten + i * spacing;
    const topOfTile = battenHeight;
    const bottomOfTile = Math.max(0, battenHeight - mainTileVariant.length);
    // Keep track of intersections with y = batten, and the edges of the shapes
    let intersections: Intersection[] = [];
    // First consider edge between final point in list and first point
    let start = vertices.length - 1;
    // Loop through each edge
    for (let end = 0; end < vertices.length; end++) {
      // Check if edge has 0 gradient
      // eslint-disable-next-line security/detect-object-injection
      if (vertices[start].y !== vertices[end].y) {
        // Gradient is not equal to zero, check that the y = batten intersects the edge
        // eslint-disable-next-line security/detect-object-injection
        if (vertices[start].y >= topOfTile !== vertices[end].y >= topOfTile) {
          let bottomOfTileX = 0;
          let topOfTileX = 0;
          // If the edge is vertical then we know the x coordinate
          // eslint-disable-next-line security/detect-object-injection
          if (vertices[start].x === vertices[end].x) {
            // eslint-disable-next-line security/detect-object-injection
            bottomOfTileX = vertices[start].x;
            // eslint-disable-next-line security/detect-object-injection
            topOfTileX = vertices[start].x;
          } else {
            // Otherwise we need to find the x coordinate - using y - y1 = m(x - x1)
            const gradient =
              // eslint-disable-next-line security/detect-object-injection
              (vertices[end].y - vertices[start].y) /
              // eslint-disable-next-line security/detect-object-injection
              (vertices[end].x - vertices[start].x);
            bottomOfTileX =
              (bottomOfTile -
                // eslint-disable-next-line security/detect-object-injection
                vertices[start].y +
                // eslint-disable-next-line security/detect-object-injection
                gradient * vertices[start].x) /
              gradient;
            topOfTileX =
              // eslint-disable-next-line security/detect-object-injection
              (topOfTile - vertices[start].y + gradient * vertices[start].x) /
              gradient;
          }
          // Add intersection point to array
          intersections.push({
            bottomOfTileX,
            topOfTileX,
            // eslint-disable-next-line security/detect-object-injection
            side: vertices[start].side
          });
        }
        // eslint-disable-next-line security/detect-object-injection
      } else if (vertices[start].y === topOfTile) {
        // If the edge has 0 gradient and intersects batten then consider it's
        // intersection points as the x points we know
        intersections.push(
          // eslint-disable-next-line security/detect-object-injection
          { x: vertices[start].x, side: vertices[start].side },
          // eslint-disable-next-line security/detect-object-injection
          { x: vertices[end].x, side: vertices[start].side }
        );
      }
      // Move the start point to the end of this edge
      start = end;
    }
    // A batten should always intersect an even number of times
    if (intersections.length % 2 === 1) {
      throw new Error("Something has gone wrong, result may not be correct");
    }
    // Sort so that smallest x is at the start of the array
    intersections = intersections.sort(function (a, b) {
      return (
        Math.max(a.topOfTileX || 0, a.bottomOfTileX || 0) -
        Math.max(b.topOfTileX || 0, b.bottomOfTileX || 0)
      );
    });

    // Take each pair - indexes (0,1) (2,3) etc and calculate the distance between them.
    for (let pair = 0; pair < intersections.length; pair += 2) {
      // Set the batten width and side types
      let width = 0;
      if (subtract) {
        // TRUE clipped batten line logic for subtract face
        width =
          (intersections[pair + 1].topOfTileX ||
            intersections[pair + 1].x ||
            // eslint-disable-next-line security/detect-object-injection
            0) - (intersections[pair].topOfTileX || intersections[pair].x || 0);
      } else {
        // Find the width of this section which is covering the roof
        width = Math.max(
          (intersections[pair + 1].topOfTileX || 0) -
            // eslint-disable-next-line security/detect-object-injection
            (intersections[pair].bottomOfTileX || 0),
          (intersections[pair + 1].topOfTileX || 0) -
            // eslint-disable-next-line security/detect-object-injection
            (intersections[pair].topOfTileX || 0),
          (intersections[pair + 1].bottomOfTileX || 0) -
            // eslint-disable-next-line security/detect-object-injection
            (intersections[pair].topOfTileX || 0),
          (intersections[pair + 1].bottomOfTileX || 0) -
            // eslint-disable-next-line security/detect-object-injection
            (intersections[pair].bottomOfTileX || 0)
        );
      }
      allBattens.push({
        index: i,
        distanceFromEave: battenHeight,
        width: width,
        // eslint-disable-next-line security/detect-object-injection
        sides: [intersections[pair].side, intersections[pair + 1].side],
        battenSpacing: spacing
      });
    }
  }

  return allBattens;
};

const getHasHalfTiles = (
  tilesInOneRow: number,
  brokenBond: boolean
): boolean => {
  if (brokenBond) {
    return Math.ceil(2 * tilesInOneRow) % 2 === 0;
  }

  const fraction = tilesInOneRow - Math.trunc(tilesInOneRow);
  return fraction >= 0.25 && fraction <= 0.75;
};

export type SurfaceProductsCount = {
  quantity: number;
  half: {
    quantity: number;
  };
  leftDoubleWingedTiles: number;
  cloakedVerge: {
    left: number;
    right: number;
    leftType9: number;
    rightType9: number;
    leftType7: number;
    rightType7: number;
    halfLeft: number;
    halfRight: number;
  };
};

export const surface = ({
  face: { battens, sides, subtract },
  tile,
  hasHalfLeftVerges,
  hasHalfRightVerges,
  cloakedVerge,
  hasDoubleWingedTile
}: {
  face: FaceWithBattens;
  tile: Tile;
  hasHalfLeftVerges: boolean;
  hasHalfRightVerges: boolean;
  cloakedVerge?: VergeOption;
  hasDoubleWingedTile: boolean;
}): SurfaceProductsCount => {
  const { halfTile: half } = tile;
  const [left, right] = sides;
  // Count the number of main tiles and half tiles
  let count = 0;
  let halfCount = 0;
  let leftDoubleWingedTilesCount = 0;
  let leftVergeCountType11 = 0;
  let rightVergeCountType11 = 0;
  let leftVergeCountType9 = 0;
  let rightVergeCountType9 = 0;
  let leftVergeCountType7 = 0;
  let rightVergeCountType7 = 0;
  let halfLeftVergeCount = 0;
  let halfRightVergeCount = 0;
  // Count tiles per each batten using tile covering width
  for (const batten of battens) {
    let row = batten.width / tile.coverWidth;
    const cloakedVergeRow = cloakedVerge
      ? (batten.width -
          getVergeTilesCoverWidth(cloakedVerge, batten.battenSpacing)) /
        tile.coverWidth
      : row;
    const hasHalfTiles = getHasHalfTiles(cloakedVergeRow, tile.brokenBond);

    if (cloakedVerge && !subtract) {
      if (tile.brokenBond) {
        if (!hasHalfTiles) {
          if (batten.index % 2 == 0) {
            if (left === "VERGE" && cloakedVerge.halfLeft) {
              halfLeftVergeCount++;
            } else if (!hasHalfLeftVerges) {
              halfCount++;
            }
            if (right === "VERGE" && cloakedVerge.halfRight) {
              halfRightVergeCount++;
            } else if (!hasHalfRightVerges) {
              halfCount++;
            }
            row -= 1;
          } else {
            if (left === "VERGE") {
              leftVergeCountType11++;
              row -= 1;
            }
            if (right === "VERGE") {
              rightVergeCountType11++;
              row -= 1;
            }
          }
        } else {
          if (batten.index % 2 == 0) {
            if (left === "VERGE") {
              leftVergeCountType11++;
            } else {
              count++;
            }
            if (right === "VERGE" && cloakedVerge.halfRight) {
              halfRightVergeCount++;
            } else if (!hasHalfRightVerges) {
              halfCount++;
            }
          } else {
            if (right === "VERGE") {
              rightVergeCountType11++;
            } else {
              count++;
            }
            if (left === "VERGE" && cloakedVerge.halfLeft) {
              halfLeftVergeCount++;
            } else if (!hasHalfLeftVerges) {
              halfCount++;
            }
          }
          row -= 1.5;
        }
      } else {
        const {
          leftVergeTileType7,
          rightVergeTileType7,
          leftVergeTileType9,
          rightVergeTileType9,
          leftVergeTileType11,
          rightVergeTileType11
        } = getVergeTiles(cloakedVerge, [left, right], batten.battenSpacing);

        leftVergeCountType7 += leftVergeTileType7;
        rightVergeCountType7 += rightVergeTileType7;
        leftVergeCountType9 += leftVergeTileType9;
        rightVergeCountType9 += rightVergeTileType9;
        leftVergeCountType11 += leftVergeTileType11;
        rightVergeCountType11 += rightVergeTileType11;

        row =
          row -
          leftVergeTileType7 -
          rightVergeTileType7 -
          leftVergeTileType9 -
          rightVergeTileType9 -
          leftVergeTileType11 -
          rightVergeTileType11;
      }
    }

    if (hasDoubleWingedTile && left === "VERGE") {
      leftDoubleWingedTilesCount++;
      row -= 1;
    }

    if (half && !subtract) {
      if (!hasHalfTiles) {
        // No decimal part - check if broken bond
        if (tile.brokenBond && !cloakedVerge && batten.index % 2 == 0) {
          // Replace one full width tile with two half tiles
          count += Math.round(row) - 1;
          halfCount += 2;
        } else {
          count += Math.round(row);
        }
      } else {
        count += Math.round(row);

        if (
          !hasHalfRightVerges &&
          !hasHalfLeftVerges &&
          ((leftVergeCountType11 && rightVergeCountType11) ||
            (leftVergeCountType9 && rightVergeCountType9) ||
            (leftVergeCountType7 && rightVergeCountType7))
        ) {
          // Increment half tile count
          // only if there is a left and right hand verge tile present
          halfCount += 1;
        }
      }
    } else {
      if (subtract) {
        // When subtracting we round DOWN to avoid missing spaces
        count -= Math.floor(batten.width / tile.coverWidth);
      } else {
        // No half tile means round UP to the nearest tile
        count += Math.round(row);
      }
    }
  }
  return {
    quantity: count,
    half: {
      quantity: halfCount
    },
    leftDoubleWingedTiles: leftDoubleWingedTilesCount,
    cloakedVerge: {
      left: leftVergeCountType11,
      right: rightVergeCountType11,
      leftType9: leftVergeCountType9,
      rightType9: rightVergeCountType9,
      leftType7: leftVergeCountType7,
      rightType7: rightVergeCountType7,
      halfLeft: halfLeftVergeCount,
      halfRight: halfRightVergeCount
    }
  };
};
